import { Badge, Icon, avatarTone, initials } from "@helmr/ui";
import { useMemo, useState, type ReactNode } from "react";
import type { PickerEntry } from "../api/auth";
import { strings } from "./strings";
import s from "./picker.module.css";

const p = strings.signIn.picker;
export const LAST_PICK_KEY = "helmr.lastPick";
/** The search box appears only when the list is long enough to need it. */
export const SEARCH_THRESHOLD = 6;

export interface LastPick { businessId: string; branchId?: string }

export function readLastPick(): LastPick | null {
  try {
    const raw = localStorage.getItem(LAST_PICK_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<LastPick> | null;
    if (!v || typeof v.businessId !== "string") return null;
    return typeof v.branchId === "string" ? { businessId: v.businessId, branchId: v.branchId } : { businessId: v.businessId };
  } catch { return null; }
}

export function writeLastPick(pick: LastPick): void {
  try { localStorage.setItem(LAST_PICK_KEY, JSON.stringify(pick)); } catch { /* storage may be blocked; the pick is a convenience */ }
}

/** Last-used gym first; the rest keep the server's order. */
export function orderGyms(entries: PickerEntry[], last: LastPick | null): PickerEntry[] {
  const i = last ? entries.findIndex((e) => e.businessId === last.businessId) : -1;
  if (i <= 0) return entries;
  return [entries[i]!, ...entries.slice(0, i), ...entries.slice(i + 1)];
}

export function filterGyms(entries: PickerEntry[], query: string): PickerEntry[] {
  const q = query.trim().toLowerCase();
  return q ? entries.filter((e) => e.name.toLowerCase().includes(q)) : entries;
}

/** "Tarnaka · Kondapur · +1 more", or a count when no names came back. */
export function branchSummary(branches: Array<{ name: string }>): string {
  if (branches.length === 0) return p.branchCount(0);
  const names = branches.slice(0, 2).map((b) => b.name).join(" · ");
  return branches.length > 2 ? `${names} · ${p.more(branches.length - 2)}` : names;
}

/** "9000022220" -> "+91 90000 22220"; anything else is shown as typed. */
export function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, "");
  const ten = d.length === 12 && d.startsWith("91") ? d.slice(2) : d.length === 10 ? d : null;
  return ten ? `+91 ${ten.slice(0, 5)} ${ten.slice(5)}` : raw.trim();
}

export function GymAvatar({ id, name }: { id: string; name: string }) {
  return <span className={`${s.av} ${s[`t${avatarTone(id)}`]}`} aria-hidden="true">{initials(name)}</span>;
}

function Chevron() {
  return <svg className={s.chev} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>;
}

function Pin() {
  return <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0C18.5 15.4 12 21 12 21zM14.5 10a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" /></svg>;
}

function Row(props: { lead: ReactNode; title: string; subtitle?: string; last: boolean; pending: boolean; disabled: boolean; onPick: () => void }) {
  return (
    <li>
      <button type="button" className={s.row} disabled={props.disabled} aria-busy={props.pending || undefined} onClick={props.onPick}>
        {props.lead}
        <span className={s.text}>
          <span className={s.name}>{props.title}</span>
          {props.subtitle ? <span className={s.sub}>{props.subtitle}</span> : null}
        </span>
        {props.last ? <Badge tone="info">{p.lastUsed}</Badge> : null}
        {props.pending ? <span className={s.spinner} role="status" aria-label={p.opening} /> : <Chevron />}
      </button>
    </li>
  );
}

export function GymPicker(props: {
  entries: PickerEntry[]; last: LastPick | null; busy: boolean; pendingId: string | null; error: string | null;
  phone: string; onPick: (e: PickerEntry) => void; onChangeNumber: () => void;
}) {
  const [query, setQuery] = useState("");
  const ordered = useMemo(() => orderGyms(props.entries, props.last), [props.entries, props.last]);
  const shown = useMemo(() => filterGyms(ordered, query), [ordered, query]);
  return (
    <div className={s.wrap}>
      <header className={s.head}>
        <h1>{p.gymTitle}</h1>
        <p className={s.signedIn}>
          {p.signedInAs(formatPhone(props.phone))}{" "}
          <button type="button" className={s.link} disabled={props.busy} onClick={props.onChangeNumber}>{p.useAnotherNumber}</button>
        </p>
      </header>
      {props.entries.length > SEARCH_THRESHOLD ? (
        <div className={s.search}>
          <Icon name="search" size={16} />
          <input type="search" aria-label={p.searchLabel} placeholder={p.searchPlaceholder} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      ) : null}
      {shown.length === 0 ? (
        <p className={s.empty} role="status">{p.noMatch(query.trim())}</p>
      ) : (
        <ul className={s.list}>
          {shown.map((e) => (
            <Row
              key={e.businessId}
              lead={<GymAvatar id={e.businessId} name={e.name} />}
              title={e.name}
              subtitle={branchSummary(e.staff?.branches ?? [])}
              last={props.last?.businessId === e.businessId}
              pending={props.pendingId === e.businessId}
              disabled={props.busy}
              onPick={() => props.onPick(e)}
            />
          ))}
        </ul>
      )}
      {props.error ? <p className={s.error} role="alert">{props.error}</p> : null}
    </div>
  );
}

export function BranchPicker(props: {
  gym: PickerEntry; last: LastPick | null; busy: boolean; pendingId: string | null; error: string | null;
  canGoBack: boolean; onBack: () => void; onPick: (branchId: string) => void;
}) {
  const lastBranch = props.last?.businessId === props.gym.businessId ? props.last.branchId : undefined;
  return (
    <div className={s.wrap}>
      {props.canGoBack ? <button type="button" className={`${s.link} ${s.back}`} disabled={props.busy} onClick={props.onBack}>{p.allGyms}</button> : null}
      <header className={s.head}>
        <div className={s.gymLine}><GymAvatar id={props.gym.businessId} name={props.gym.name} /><span className={s.gymName}>{props.gym.name}</span></div>
        <h1>{strings.signIn.branchTitle}</h1>
      </header>
      <ul className={s.list}>
        {props.gym.staff!.branches.map((b) => (
          <Row
            key={b.id}
            lead={<span className={s.pin}><Pin /></span>}
            title={b.name}
            last={lastBranch === b.id}
            pending={props.pendingId === b.id}
            disabled={props.busy}
            onPick={() => props.onPick(b.id)}
          />
        ))}
      </ul>
      {props.error ? <p className={s.error} role="alert">{props.error}</p> : null}
    </div>
  );
}
